import { useEffect } from "react";
import { toast } from "sonner";

import { noteSignedOutAfter } from "@/lib/auth/device";
import {
  ACTIVITY_EVENTS,
  ACTIVITY_KEY,
  planIdle,
  readLastActivity,
  recordActivity,
} from "@/lib/auth/idle";
import { useSignInSettings } from "@/lib/auth/signInSettings";

/**
 * Signs a tab out after a quiet spell.
 *
 * The counter tablets are shared, and somebody who walks away without
 * signing out leaves their record on the screen for the next person. So
 * every signed-in tab watches for a pointer, a key, a scroll or a touch,
 * writes the moment down where the other tabs can see it, and when the
 * silence reaches HR's limit it signs out and leaves a line for the sign-in
 * page to say why. A minute before, it asks.
 *
 * It renders nothing. The auth provider mounts it whenever there is a
 * session, so it is there for every signed-in page, the status pages
 * included: a tablet is a tablet whatever the account on it is waiting for.
 * The provider hands it the way out rather than being asked for it, so the
 * two do not import each other.
 */
export function IdleSignOut({ signOut }: { signOut: () => Promise<void> }) {
  const { data } = useSignInSettings();
  useIdleSignOut(data?.auto_sign_out_minutes ?? 0, signOut);
  return null;
}

function useIdleSignOut(minutes: number, signOut: () => Promise<void>) {
  useEffect(() => {
    if (minutes <= 0) return;
    const limitMs = minutes * 60_000;

    let timer: ReturnType<typeof setTimeout> | undefined;
    let warning: string | number | undefined;
    let finished = false;

    const dismissWarning = () => {
      if (warning === undefined) return;
      toast.dismiss(warning);
      warning = undefined;
    };

    const leave = () => {
      if (finished) return;
      finished = true;
      clearTimeout(timer);
      dismissWarning();
      noteSignedOutAfter(minutes);
      void signOut();
    };

    // Always from the stamp, never from how long the timer thought it
    // waited: a background tab's timers run late, and a tab coming back
    // after twenty minutes should know it at once.
    const check = () => {
      if (finished) return;
      clearTimeout(timer);
      const now = Date.now();
      const plan = planIdle(readLastActivity(now), now, limitMs);
      if (plan.stage === "expired") {
        leave();
        return;
      }
      if (plan.stage === "warning") {
        warning ??= toast("Still there?", {
          description: "This tab signs out in a minute if nothing is touched.",
          duration: plan.remaining,
          action: {
            label: "Stay signed in",
            onClick: () => {
              recordActivity();
              check();
            },
          },
        });
      } else {
        dismissWarning();
      }
      timer = setTimeout(check, plan.wait);
    };

    // A touch within a second of the last is not written down, and there is
    // nothing to reschedule for it either.
    const touched = () => {
      if (recordActivity()) check();
    };
    // Coming back to the tab is not a touch: whoever is looking now may not
    // be whoever was. It is a reason to look at the clock, which a hidden
    // tab's timer may not have done.
    const looked = () => {
      if (document.visibilityState === "visible") check();
    };
    // Another tab was touched, or cleared the stamp.
    const heard = (event: StorageEvent) => {
      if (event.key === ACTIVITY_KEY || event.key === null) check();
    };

    // Captured, so a scroll inside a panel counts: scroll does not bubble.
    for (const name of ACTIVITY_EVENTS) {
      window.addEventListener(name, touched, { capture: true, passive: true });
    }
    document.addEventListener("visibilitychange", looked);
    window.addEventListener("storage", heard);
    check();

    return () => {
      finished = true;
      clearTimeout(timer);
      dismissWarning();
      for (const name of ACTIVITY_EVENTS) {
        window.removeEventListener(name, touched, { capture: true });
      }
      document.removeEventListener("visibilitychange", looked);
      window.removeEventListener("storage", heard);
    };
  }, [minutes, signOut]);
}
